import {
  BookOpenText,
  GraduationCap,
  IdentificationCard,
  ListStar,
  MapPin,
  Translate,
} from "@phosphor-icons/react";

import LoadableImage from "common/components/ui/LoadableImage";
import type { DetailedLead } from "common/types";

import useLead from "../../../datahooks/useLead";
import { getCompaniesFromWorkExperience } from "../../../utils";
import BaseLeadDetailTag from "./BaseLeadDetailTag";

import type { JSX } from "react";

import CompanyFallbackImage from "assets/images/profile-placeholders/company.png";

const leadDetailsKeys: Record<
  keyof Pick<
    DetailedLead,
    | "interests"
    | "location"
    | "skills"
    | "languages"
    | "education"
    | "current_experience"
    | "work_experience"
    | "occupation"
  >,
  (detailedLead: DetailedLead) => {
    icon: JSX.Element;
    label: string;
    extraItems: string[];
  }
> = {
  current_experience: ({ current_experience }) => {
    const companiesArray = Object.entries(
      getCompaniesFromWorkExperience(current_experience),
    ).filter(([name]) => !!name);

    const firstCurrentCompany = companiesArray[0]?.[1][0].company;
    return {
      icon: firstCurrentCompany && (
        <LoadableImage
          src={firstCurrentCompany.logo_url}
          fallbackImage={CompanyFallbackImage}
          className="rounded size-4"
        />
      ),
      label: firstCurrentCompany
        ? firstCurrentCompany.name
        : "No current company",
      extraItems: companiesArray.slice(1).map(([companyName]) => companyName),
    };
  },
  work_experience: ({ work_experience, current_experience }) => {
    const currentCompaniesNames = Object.keys(
      getCompaniesFromWorkExperience(current_experience),
    );
    const companiesArray = Object.entries(
      getCompaniesFromWorkExperience(work_experience),
    ).filter(([name]) => !!name && !currentCompaniesNames.includes(name));

    const firstPastCompany = companiesArray[0]?.[1][0].company;
    return {
      icon: firstPastCompany && (
        <LoadableImage
          src={firstPastCompany.logo_url}
          fallbackImage={CompanyFallbackImage}
          className="rounded size-4"
        />
      ),
      label: firstPastCompany ? firstPastCompany.name : "No past companies",
      extraItems: companiesArray.slice(1).map(([companyName]) => companyName),
    };
  },
  education: ({ education }) => {
    return {
      icon: <GraduationCap weight="fill" />,
      label: education.length ? education[0].school.name : "No education",
      extraItems: education.slice(1).map(({ school }) => school.name),
    };
  },
  location: ({ location }) => {
    return {
      icon: <MapPin weight="fill" />,
      label: location ? location.name : "No location",
      extraItems: [],
    };
  },
  interests: ({ interests }) => {
    return {
      icon: <ListStar weight="fill" />,
      label: interests.length ? interests[0].name : "No interests",
      extraItems: interests.slice(1).map(({ name }) => name),
    };
  },
  skills: ({ skills }) => {
    return {
      icon: <BookOpenText weight="fill" />,
      label: skills.length ? skills[0].name : "No skills",
      extraItems: skills.slice(1).map(({ name }) => name),
    };
  },
  languages: ({ languages }) => {
    return {
      icon: <Translate weight="fill" />,
      label: languages.length ? languages[0].name : "No languages",
      extraItems: languages.slice(1).map(({ name }) => name),
    };
  },
  occupation: ({ occupation }) => {
    return {
      icon: <IdentificationCard weight="fill" />,
      label: occupation ? occupation : "No occupation",
      extraItems: [],
    };
  },
};

interface LeadDetailTagProps {
  leadId: string;
  leadKey: keyof typeof leadDetailsKeys;
  isLoading?: boolean;
}

export default function LeadDetailTag({
  leadKey,
  leadId,
  isLoading,
}: LeadDetailTagProps) {
  const { lead, isLoadingLead } = useLead({ profileId: leadId });

  let leftComponent: JSX.Element, label: string, extraItems: string[];

  if (lead) {
    const data = leadDetailsKeys[leadKey](lead);
    leftComponent = data.icon;
    extraItems = data.extraItems;
    label = data.label;
  }

  return (
    <BaseLeadDetailTag
      isLoading={isLoadingLead || isLoading}
      leftComponent={leftComponent}
      extraItems={extraItems}
    >
      {label}
    </BaseLeadDetailTag>
  );
}
